import React, { useState, useEffect, useCallback, useRef } from 'react';
import QRCode from 'react-qr-code';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, QrCode, Smartphone, RefreshCw, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import {
    initiateUpiIntent,
    getPaymentStatus,
    buildUpiUri,
    UPI_APPS,
    type UpiSdkParams,
} from '../services/juspayApi';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SheetState =
    | 'idle'
    | 'initiating'
    | 'qr_ready'
    | 'polling'
    | 'success'
    | 'failed'
    | 'error';

interface JuspayPaymentSheetProps {
    /** Juspay order_id returned by /api/lms/payment/initiate/ */
    orderId: string;
    /** Stable customer identifier (phone number) */
    customerId: string;
    /** Payment amount in INR */
    amount: number;
    /** Payment action label for display */
    action: 'PAY' | 'CANCEL' | 'FORECLOSE';
    /** Called when payment is confirmed CHARGED */
    onSuccess: () => void;
    /** Called when user cancels or payment fails */
    onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 3000;       // 3 seconds between polls
const POLL_MAX_DURATION_MS = 600000; // 10 minutes max polling
const QR_EXPIRY_SECONDS = 600;       // 10 minutes QR validity

// Detect mobile device
const isMobileDevice = () =>
    /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent) ||
    window.innerWidth < 768;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const JuspayPaymentSheet: React.FC<JuspayPaymentSheetProps> = ({
    orderId,
    customerId,
    amount,
    action,
    onSuccess,
    onCancel,
}) => {
    const [sheetState, setSheetState] = useState<SheetState>('idle');
    const [sdkParams, setSdkParams] = useState<UpiSdkParams | null>(null);
    const [upiUri, setUpiUri] = useState<string>('');
    const [qrExpiry, setQrExpiry] = useState<number>(QR_EXPIRY_SECONDS);
    const [activeTab, setActiveTab] = useState<string>(isMobileDevice() ? 'apps' : 'qr');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pollStartRef = useRef<number>(0);
    const qrCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // -----------------------------------------------------------------------
    // Cleanup on unmount
    // -----------------------------------------------------------------------
    useEffect(() => {
        return () => {
            stopPolling();
            stopQrCountdown();
        };
    }, []);

    // -----------------------------------------------------------------------
    // Auto-initiate on mount
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (orderId && sheetState === 'idle') {
            handleInitiate();
        }
    }, [orderId]);

    // -----------------------------------------------------------------------
    // Initiate UPI Intent
    // -----------------------------------------------------------------------
    const handleInitiate = useCallback(async (upiApp = '') => {
        setSheetState('initiating');
        setErrorMessage('');
        stopPolling();
        stopQrCountdown();

        try {
            const result = await initiateUpiIntent(orderId, customerId, upiApp);
            const params = result.payment?.sdk_params;

            if (!params?.tr || !params?.merchant_vpa) {
                throw new Error('Invalid sdk_params received from Juspay');
            }

            const uri = buildUpiUri(params, actionLabel(action));
            setSdkParams(params);
            setUpiUri(uri);
            setSheetState('qr_ready');
            setQrExpiry(QR_EXPIRY_SECONDS);

            startQrCountdown();
            startPolling();
        } catch (err: any) {
            const msg = err?.response?.data?.error || err?.message || 'Failed to initiate UPI payment';
            setErrorMessage(msg);
            setSheetState('error');
            toast.error(msg);
        }
    }, [orderId, customerId, action]);

    // -----------------------------------------------------------------------
    // Polling
    // -----------------------------------------------------------------------
    const startPolling = useCallback(() => {
        pollStartRef.current = Date.now();
        setSheetState('polling');

        pollTimerRef.current = setInterval(async () => {
            // Stop after max duration
            if (Date.now() - pollStartRef.current > POLL_MAX_DURATION_MS) {
                stopPolling();
                setSheetState('error');
                setErrorMessage('Payment session expired. Please try again.');
                return;
            }

            try {
                const statusData = await getPaymentStatus(orderId);
                const pgStatus = statusData.status;

                if (pgStatus === 'CHARGED') {
                    stopPolling();
                    stopQrCountdown();
                    setSheetState('success');
                    toast.success('Payment successful!');
                    setTimeout(() => onSuccess(), 1500);
                } else if (pgStatus === 'AUTHORIZATION_FAILED') {
                    stopPolling();
                    stopQrCountdown();
                    setSheetState('failed');
                    toast.error('Payment failed. Please try again.');
                }
                // PENDING_VBV / NEW — keep polling
            } catch {
                // Network error during poll — keep trying silently
            }
        }, POLL_INTERVAL_MS);
    }, [orderId, onSuccess]);

    const stopPolling = () => {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    };

    // -----------------------------------------------------------------------
    // QR countdown
    // -----------------------------------------------------------------------
    const startQrCountdown = () => {
        qrCountdownRef.current = setInterval(() => {
            setQrExpiry(prev => {
                if (prev <= 1) {
                    stopQrCountdown();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const stopQrCountdown = () => {
        if (qrCountdownRef.current) {
            clearInterval(qrCountdownRef.current);
            qrCountdownRef.current = null;
        }
    };

    const formatExpiry = (seconds: number) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    // -----------------------------------------------------------------------
    // UPI App deep-link handler
    // -----------------------------------------------------------------------
    const handleUpiAppClick = async (packageName: string) => {
        // Re-initiate with the specific app package to get app-specific sdk_params
        await handleInitiate(packageName);

        // After initiation, open the deep-link
        if (upiUri) {
            window.location.href = upiUri;
        }
    };

    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------
    const actionLabel = (a: string) => {
        switch (a) {
            case 'CANCEL': return 'Loan Cancellation';
            case 'FORECLOSE': return 'Loan Foreclosure';
            default: return 'Loan Repayment';
        }
    };

    const isLoading = sheetState === 'initiating';
    const isQrReady = sheetState === 'qr_ready' || sheetState === 'polling';
    const isSuccess = sheetState === 'success';
    const isFailed = sheetState === 'failed';
    const isError = sheetState === 'error';

    // -----------------------------------------------------------------------
    // Render: Success
    // -----------------------------------------------------------------------
    if (isSuccess) {
        return (
            <div className="flex flex-col items-center justify-center py-8 gap-4 text-center">
                <div className="bg-emerald-100 p-4 rounded-full">
                    <CheckCircle2 className="h-12 w-12 text-emerald-600" />
                </div>
                <div>
                    <p className="text-xl font-bold text-emerald-700">Payment Successful!</p>
                    <p className="text-sm text-slate-500 mt-1">₹{amount.toFixed(2)} received</p>
                </div>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // Render: Failed
    // -----------------------------------------------------------------------
    if (isFailed) {
        return (
            <div className="flex flex-col items-center justify-center py-8 gap-4 text-center">
                <div className="bg-rose-100 p-4 rounded-full">
                    <XCircle className="h-12 w-12 text-rose-600" />
                </div>
                <div>
                    <p className="text-xl font-bold text-rose-700">Payment Failed</p>
                    <p className="text-sm text-slate-500 mt-1">Your payment could not be processed.</p>
                </div>
                <Button onClick={() => handleInitiate()} variant="outline" className="mt-2">
                    <RefreshCw className="h-4 w-4 mr-2" /> Try Again
                </Button>
                <Button variant="ghost" onClick={onCancel} className="text-slate-500">
                    Cancel
                </Button>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // Render: Error
    // -----------------------------------------------------------------------
    if (isError) {
        return (
            <div className="flex flex-col items-center justify-center py-8 gap-4 text-center">
                <Alert className="bg-rose-50 border-rose-200 text-rose-800 text-sm">
                    <AlertDescription>{errorMessage || 'Something went wrong. Please try again.'}</AlertDescription>
                </Alert>
                <Button onClick={() => handleInitiate()} variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" /> Retry
                </Button>
                <Button variant="ghost" onClick={onCancel} className="text-slate-500">
                    Cancel
                </Button>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // Render: Loading
    // -----------------------------------------------------------------------
    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-slate-500">Preparing UPI payment...</p>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // Render: Main UPI sheet
    // -----------------------------------------------------------------------
    return (
        <div className="space-y-4">
            {/* Amount header */}
            <div className="text-center pb-2 border-b">
                <p className="text-xs font-black uppercase tracking-widest text-slate-400">
                    {actionLabel(action)}
                </p>
                <p className="text-3xl font-black text-slate-900 mt-1">
                    ₹{amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <Badge variant="outline" className="mt-1 text-[10px] text-emerald-700 border-emerald-200 bg-emerald-50">
                    Pay via UPI
                </Badge>
            </div>

            {/* Polling indicator */}
            {sheetState === 'polling' && (
                <div className="flex items-center gap-2 text-[11px] text-slate-400 justify-center">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                    Waiting for payment confirmation...
                </div>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="qr" className="flex items-center gap-1.5 text-xs">
                        <QrCode className="h-3.5 w-3.5" /> Scan QR
                    </TabsTrigger>
                    <TabsTrigger value="apps" className="flex items-center gap-1.5 text-xs">
                        <Smartphone className="h-3.5 w-3.5" /> UPI Apps
                    </TabsTrigger>
                </TabsList>

                {/* ---- QR Tab ---- */}
                <TabsContent value="qr" className="mt-4">
                    {isQrReady && upiUri ? (
                        <div className="flex flex-col items-center gap-4">
                            <div className="p-3 bg-white border-2 border-slate-100 rounded-xl shadow-sm">
                                <QRCode
                                    value={upiUri}
                                    size={200}
                                    level="M"
                                    style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                                />
                            </div>

                            {/* Expiry countdown */}
                            <div className={`flex items-center gap-1.5 text-xs font-medium ${qrExpiry < 60 ? 'text-rose-500' : 'text-slate-400'}`}>
                                <Clock className="h-3.5 w-3.5" />
                                {qrExpiry > 0
                                    ? `QR expires in ${formatExpiry(qrExpiry)}`
                                    : 'QR expired'}
                            </div>

                            {qrExpiry === 0 && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleInitiate()}
                                    className="text-xs"
                                >
                                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh QR
                                </Button>
                            )}

                            <p className="text-[11px] text-slate-400 text-center px-4">
                                Open any UPI app — GPay, PhonePe, BHIM — and scan this QR code
                            </p>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center py-8 gap-3">
                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                            <p className="text-sm text-slate-400">Generating QR code...</p>
                        </div>
                    )}
                </TabsContent>

                {/* ---- UPI Apps Tab ---- */}
                <TabsContent value="apps" className="mt-4">
                    <div className="space-y-3">
                        <p className="text-[11px] text-slate-400 text-center">
                            Tap your UPI app to pay directly
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                            {UPI_APPS.map((app) => (
                                <Button
                                    key={app.packageName}
                                    variant="outline"
                                    className="h-16 flex flex-col gap-1 border-2 hover:border-primary hover:bg-primary/5 transition-all"
                                    onClick={() => handleUpiAppClick(app.packageName)}
                                    disabled={isLoading}
                                >
                                    <span className="text-2xl">{app.icon}</span>
                                    <span className="text-xs font-semibold">{app.name}</span>
                                </Button>
                            ))}
                        </div>

                        {/* Fallback: if on desktop, show note */}
                        {!isMobileDevice() && (
                            <Alert className="bg-amber-50 border-amber-200 text-amber-800 text-[11px] mt-2">
                                <AlertDescription>
                                    UPI app deep-links work on mobile devices. Use the <strong>Scan QR</strong> tab on desktop.
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>
                </TabsContent>
            </Tabs>

            {/* Cancel button */}
            <div className="pt-2 border-t">
                <Button
                    variant="ghost"
                    className="w-full text-slate-500 text-sm"
                    onClick={() => {
                        stopPolling();
                        stopQrCountdown();
                        onCancel();
                    }}
                >
                    Cancel Payment
                </Button>
            </div>
        </div>
    );
};

export default JuspayPaymentSheet;
